// Disable eslint because this code was not written with lint in mind, and was ported in. It needs to be fixed at some point.
/* eslint-disable */
import { RequestManager } from '../request_manager';
import { IUser, UserHeader } from '../user';
import { EmailToken, EmailTokenParameters } from './email';
import { Token, TokenClass, TokenParameters, TokenType } from './token';

export class YouVersionTokenParameters extends TokenParameters {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly data: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(data: any) {
    super();
    if (!data['created']) {
      data['created'] = new Date().getTime();
    }
    this.data = data;
  }

  expire() {
    this.data['expires_in'] = 0;
  }

  get expiresAt(): number {
    return this.data['created'] + this.data['expires_in'] * 1000;
  }

  get refreshToken(): string | null {
    return this.data['refresh_token'];
  }

  get accessToken(): string | null {
    return this.data['access_token'];
  }

  get scope(): string | null {
    return this.data['scope'];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toStorageItem(user: IUser): any {
    return {
      type: TokenClass.youversion,
      id: user.id,
      username: user.username,
      email: user.email,
      data: this.data,
    };
  }

  static fromStorageItem(
    clientId: string,
    clientSecret: string,
    requestManager: RequestManager,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any
  ): YouVersionToken {
    let email = item['email'];
    if (!email) {
      email = '';
    }
    return new YouVersionToken(
      clientId,
      clientSecret,
      requestManager,
      new UserHeader(item['id'], item['username'], email),
      new YouVersionTokenParameters(item['data'])
    );
  }
}

export class YouVersionToken extends Token {
  private readonly clientId: string;
  private readonly clientSecret: string;
  private readonly requestManager: RequestManager;
  private _invalid = false;
  private _user: IUser;
  private parameters: YouVersionTokenParameters;
  private originatingToken: Token | null;
  private _initialized = false;
  private _onRefreshed: (token: Token) => void;

  constructor(
    clientId: string,
    clientSecret: string,
    requestManager: RequestManager,
    user: IUser,
    parameters: YouVersionTokenParameters
  ) {
    super(TokenType.actual, TokenClass.youversion);
    this._user = user;
    this.requestManager = requestManager;
    this.parameters = parameters;
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.originatingToken = null;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    this._onRefreshed = () => {};
  }

  initialize(originatingToken: Token | null, onRefreshed: (token: Token) => void) {
    this.originatingToken = originatingToken;
    this._initialized = true;
    this._onRefreshed = onRefreshed;
  }

  get scopes(): Array<string> {
    if (this.parameters.scope == null) {
      return [];
    }
    return this.parameters.scope.split(' ');
  }

  get initialized(): boolean {
    return this._initialized;
  }

  get user(): IUser {
    return this._user;
  }

  set user(user: IUser) {
    this._user = user;
  }

  get token(): string | null {
    return this.parameters.accessToken;
  }

  toParameters(): TokenParameters {
    return this.parameters;
  }

  get refreshable(): boolean {
    return this.originatingToken != null || this.parameters.refreshToken != null;
  }

  get refreshToken(): string | null {
    return this.parameters.refreshToken;
  }

  async exchange(clientId: string, clientSecret: string, scopes: Array<string>): Promise<Token> {
    if (this.refreshToken == null) {
      return Promise.reject('Missing refresh token');
    }
    const subjToken = encodeURIComponent(this.refreshToken);
    let parameters = `client_id=${encodeURIComponent(clientId)}&client_secret=${encodeURIComponent(
      clientSecret
    )}&grant_type=urn:ietf:params:oauth:grant-type:token-exchange&subject_token=${subjToken}&subject_token_type=urn:ietf:params:oauth:token-type:refresh_token`;
    if (scopes.length > 0) {
      parameters += `&scope=${encodeURIComponent(scopes.join(' '))}`;
    }
    const body = await this.requestManager.postForm('/as/token', parameters);
    const tokenParameters = new YouVersionTokenParameters(body);
    return new YouVersionToken(clientId, clientSecret, this.requestManager, this.user, tokenParameters);
  }

  expire(): Promise<void> {
    this.parameters.expire();
    return Promise.resolve(undefined);
  }

  get expired(): boolean {
    return (
      this.token == null ||
      this.token.length == 0 ||
      this.refreshToken == null ||
      this.refreshToken.length == 0 ||
      new Date().getTime() >= this.expires
    );
  }

  get expires(): number {
    return this.parameters.expiresAt;
  }

  setValid(): Promise<void> {
    this._invalid = false;
    return Promise.resolve(undefined);
  }

  get header(): string | null {
    if (this.parameters.accessToken == null) {
      return null;
    }
    return `Bearer ${this.parameters.accessToken}`;
  }

  get invalid(): boolean {
    return this._invalid;
  }

  get originatingId(): string | null {
    return this.user.username;
  }

  async refresh(): Promise<Token> {
    if (this.invalid) {
      throw new Error("Failed to refresh token, because it's invalid");
    }
    await this.refreshWithToken(this.refreshToken, true);
    return this;
  }

  private async toOriginatingTokenParameters(parameters: string): Promise<string> {
    if (this.originatingToken == null) {
      throw new Error('Originating token is required');
    }
    if (this.originatingToken.refreshable && this.originatingToken.expired) {
      this.originatingToken = await this.originatingToken.refresh();
    }
    const tokenClass = this.originatingToken == null ? null : this.originatingToken.tokenClass;
    switch (tokenClass) {
      case TokenClass.email: {
        const emailToken = this.originatingToken as EmailToken;
        const username = emailToken.user.username;
        const password = ((await emailToken.toParameters()) as EmailTokenParameters).password;
        return `${parameters}&grant_type=password&username=${encodeURIComponent(
          username
        )}&password=${encodeURIComponent(password)}`;
      }
      case TokenClass.scopedEmail: {
        const emailToken = this.originatingToken as EmailToken;
        const username = emailToken.user.username;
        const password = ((await emailToken.toParameters()) as EmailTokenParameters).password;
        return `${parameters}&grant_type=password&username=${encodeURIComponent(
          username
        )}&password=${encodeURIComponent(password)}&scope=${encodeURIComponent(emailToken.scopes.join(' '))}`;
      }
      case TokenClass.google:
      case TokenClass.facebook:
      case TokenClass.apple: {
        const token = this.originatingToken.token;
        return `${parameters}&grant_type=password&${tokenClass}=${token}`;
      }
      case TokenClass.youversion: {
        if (this.originatingToken.refreshToken == null) {
          throw new Error("You can't refresh a youversion token that doesn't have a refresh token.");
        }
        return `${parameters}&grant_type=refresh_token&refresh_token=${this.originatingToken.refreshToken}`;
      }
      default:
        throw new Error('Unsupported token class');
    }
  }

  private async toRequestParameters(refreshToken: string | null): Promise<string> {
    let parameters = `client_id=${encodeURIComponent(this.clientId)}&client_secret=${encodeURIComponent(
      this.clientSecret
    )}`;
    if (this.parameters.accessToken == null || refreshToken == null || refreshToken.length == 0) {
      return this.toOriginatingTokenParameters(parameters);
    } else {
      parameters = `${parameters}&grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`;
      if (this.scopes.length > 0) {
        parameters += `&scope=${encodeURIComponent(this.scopes.join(' '))}`;
      }
      return parameters;
    }
  }

  private async refreshWithToken(refreshToken: string | null, allowRetry: boolean): Promise<void> {
    try {
      const parameters = await this.toRequestParameters(refreshToken);
      const response = await this.requestManager.postForm('/token', parameters);
      this.parameters = new YouVersionTokenParameters(response);
      this._onRefreshed(this);
    } catch (e) {
      await this.onRefreshFailed(allowRetry, e);
      return;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async onRefreshFailed(allowRetry: boolean, error: any): Promise<void> {
    if (allowRetry) {
      try {
        this.originatingToken =
          this.originatingToken == null ? null : ((await this.originatingToken.refresh()) ?? null);
        await this.refreshWithToken(null, false);
      } catch (e) {
        await this.onRefreshFailed(false, e);
      }
    } else {
      this._invalid = true;
      throw new Error(`Failed to refresh token: ${error.message}, not retrying`);
    }
  }

  private get expirationBuffer(): number {
    if (this.requestManager.staging) {
      return 20000;
    } else {
      return 86400000;
    }
  }

  get willExpireSoon(): boolean {
    return this.expired || this.expires <= new Date().getTime() + this.expirationBuffer;
  }
}
